import {
  easeInOutSine,
  smoothstep,
  clamp,
  w, h,
  frame,
  frameN,
  setFrameN,
  getCanvasSize,
  setupCommon,
  drawCommon,
  make,
} from '../js/common.js';
const polygonClipping = require('./polygon-clipping.umd.min.js');

let font;
const fontUrl = new URL('./RobotoMono-Medium.ttf', import.meta.url);
export function preload() {
	font = loadFont(fontUrl.href);
}

export function setup() {
	setupCommon();

	textFont(font);

	noSmooth();
}

function makeRect(cx, cy, s, t) {
	const s2 = s / 2;
	const points = [...Array(4).keys()].map(i => {
		const theta = (i / 4) * (2 * Math.PI) + t;
		const x = cx + s2 * Math.cos(theta);
		const y = cy + s2 * Math.sin(theta);
		return [x, y];
	});
	points.push(points[0]);
	return points;
}

function makeChaikin() {
	// https://github.com/georgedoescode/chaikin
	//
	// linear interpolation, return a point(t) between start and end
	function _lerp(start, end, t) {
		return start * (1 - t) + end * t;
	}

	// returned 2 points "sliced" between original 2 points
	function _chaikinCut(a, b, ratio) {
		return [
			{ x: _lerp(a.x, b.x, ratio), y: _lerp(a.y, b.y, ratio) },
			{ x: _lerp(b.x, a.x, ratio), y: _lerp(b.y, a.y, ratio) }
		];
	}

	return function(shape, ratio, iterations, close) {
		// no more iterations left, return the result
		if (iterations === 0) return shape;

		// next shape iteration
		const next = [];
		// corner = each vertex ({x, y}) in shape
		const corners = Object.keys(shape);
		const numCorners = close ? corners.length : corners.length - 1;

		for (let i = 0; i < numCorners; i++) {
			// side + adjacent side
			const a = shape[corners[i]];
			const b = shape[corners[(i + 1) % corners.length]];
			// "cut" points between each side
			const n = _chaikinCut(a, b, ratio);

			if (!close && i === 0) {
				// ignore the "cut" A vertex for start point of open shapes
				next.push({ x: a.x, y: a.y });
				next.push({ x: n[1].x, y: n[1].y });
			} else if (!close && i === numCorners - 1) {
				// ignore the "cut" B vertex for end point of open shapes
				next.push({ x: n[0].x, y: n[0].y });
				next.push({ x: b.x, y: b.y });
			} else {
				// add both cut vertices
				next.push({ x: n[0].x, y: n[0].y });
				next.push({ x: n[1].x, y: n[1].y });
			}
		}

		return chaikin(next, ratio, iterations - 1, close);
	};
}

const chaikin = makeChaikin();

function drawPoly(poly, isClosed) {
	beginShape();
	let points = !!isClosed ? poly : poly.slice(0, -1);
	points
		.forEach(([x, y]) => {
			return vertex(w(x), h(y))
		});
	endShape(CLOSE);
}

function makeText(s, x, y, t) {
	const textSizeR = 0.015;
	const chars = Math.floor(1.0 / textSizeR);
	const offset = Math.floor(t * 100);

	textAlign(LEFT);
	textSize(w(textSizeR));
	fill(0, 0, 10, 0.5);
	noStroke();

	for (let i = 0; i < s.length; i += chars) {
		const n = noise(t, i * 0.001);

		const start = i + offset;
		const lengthCap = 15 + Math.floor(n * 20);
		const end = start + chars * 0.5 + lengthCap;
		const line = s.slice(start, end)

		if (n > 0.5)
			text(line,
				w(0.05 - 0.5 + Math.floor(n * 30) / 30),
				h(0.02 +
					(10 * -0.025) +
					0.025 * (i / chars) +
					0.025 * Math.floor(1 * Math.sin(3 * t * PI))))
	}
}

export function draw() {
	drawCommon();
	blendMode(BLEND);
	background(10);

	const t = TAU * frameN;
	const is = 0.2 + 0.05 * Math.sin(t);
	const a1 = [makeRect(0.3, 0.3, 0.5, t), makeRect(0.3, 0.3, is, t)];
	const a2 = [makeRect(0.7, 0.7, 0.5, t * -2), makeRect(0.7, 0.7, is * 1.5, -t)];
	const a3 = [makeRect(0.3, 0.7, 0.5, t * 1), makeRect(0.3, 0.7, is, -t)];
	const a4 = [makeRect(0.7, 0.3, 0.5, t * 2), makeRect(0.7, 0.3, 0.2, t)];
	const a5 = [makeRect(0.1, 0.1, 0.4, -0.5 * t)];
	const a6 = [makeRect(0.85, 0.85, 0.4, -1.75 * t)];
	const a7 = [makeRect(0.05, 0.95, 0.4, 1.25 * t)];
	const a8 = [makeRect(0.55, 0.95, 0.4, 1.5 * t)];

	const b = [makeRect(0.5, 0.5, 0.7 + 0.2 * Math.sin(t), QUARTER_PI), makeRect(0.5, 0.5, 0.3, QUARTER_PI)];
	const clip = polygonClipping.xor(b, a1, a2, a3, a4, a5, a6, a7, a8);

	translate(w(0.05), h(-0.05));
	stroke(150, 100, 100, 1);
	fill(0, 0, 95, 1);
	clip.forEach(poly => {
		poly.forEach(poly => {
			let points = poly.map(([x, y]) => ({ x, y }));
			points = chaikin(points, 0.25, 2, false);
			points = points.map(({ x, y }) => ([x, y]));
			drawPoly(points);
		});
	});
	noFill()
	stroke(0, 100, 100);
	clip.forEach(poly => poly.forEach(drawPoly));
	resetMatrix();

	makeText(JSON.stringify(clip), w(0.5), h(0.5), frameN);

	fill(0, 0, 100);
	stroke(0);
	rect(w(0.775), h(0.025), w(0.2), h(0.14));
	noStroke();
	fill(0);
	textSize(w(0.02));
	text(` Frame:  ${frame}`, w(0.775), h(0.05));
	text(` FrameN: ${frameN.toFixed(3)}`, w(0.775), h(0.075));
	text(` FPS:    ${frameRate().toFixed(3)}`, w(0.775), h(0.1));
	text(` Time:   ${deltaTime.toFixed(0)} ms`, w(0.775), h(0.125));
	text(` smooth: false`, w(0.775), h(0.15));
}
